import { api } from "./api";
import { fetchTokenService as fetchAmsToken } from "core/services/agentMySecurity/authService";

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

export interface ChatRequest {
  messages: ChatMessage[];
  session_id?: string;
  project_id?: string;
  stream?: boolean;
}

type ApiMessageResponse = {
  content?: string;
  response?: string;
  conversationid?: string;
  chatid?: string;
  [key: string]: any;
};

type ApiFileUploadResponse = {
  content?: string;
  response?: string;
  conversationid?: string;
  chatid?: string;
  [key: string]: any;
};

const generateId = (): string => {
  const maybeCrypto = globalThis.crypto as Crypto | undefined;
  if (maybeCrypto?.randomUUID) return maybeCrypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const chatService = {
  async getConversationViaApiConversations(params: {
    conversationid: string;
  }): Promise<any | null> {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/conversations/:id");
    }

    const resp = await api.get<{ conversations?: any[] }>(
      `/api/conversations/${params.conversationid}`,
      {
        timeout: 30000,
        headers: {
          Authorization: authHeaderValue,
        },
        withCredentials: true,
      }
    );

    return resp.data?.conversations?.[0] ?? null;
  },

  async getConversationSignedUrlsViaApiConversations(params: {
    conversationid: string;
  }): Promise<
    Array<{
      path?: string;
      signedUrl?: string;
      expiresAt?: string;
      conversationId?: string;
      [key: string]: any;
    }>
  > {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/conversations/:id/signed-urls");
    }

    const resp = await api.get<any[]>(
      `/api/conversations/${params.conversationid}/signed-urls`,
      {
        timeout: 30000,
        headers: {
          Authorization: authHeaderValue,
        },
        withCredentials: true,
      }
    );

    return Array.isArray(resp.data) ? resp.data : [];
  },

  async updateConversationTitleViaApiConversations(params: {
    conversationid: string;
    conversationTitle: string;
  }): Promise<void> {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/conversations/:id");
    }

    await api.put(
      `/api/conversations/${params.conversationid}`,
      { conversationTitle: params.conversationTitle },
      {
        timeout: 30000,
        headers: {
          Authorization: authHeaderValue,
        },
        withCredentials: true,
      }
    );
  },

  async listConversationsViaApiConversations(): Promise<any[]> {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/conversations");
    }

    const resp = await api.get<{ conversations?: any[] }>("/api/conversations", {
      timeout: 30000,
      headers: {
        Authorization: authHeaderValue,
      },
      withCredentials: true,
    });

    return resp.data?.conversations ?? [];
  },

  async deleteConversationViaApiConversations(params: {
    conversationId: string;
  }): Promise<void> {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for DELETE /api/conversations");
    }

    const conversationId = (params.conversationId || "").trim();
    if (!conversationId) return;

    await api.delete("/api/conversations", {
      timeout: 30000,
      headers: {
        Authorization: authHeaderValue,
      },
      withCredentials: true,
      data: {
        conversationId,
      },
    });
  },

  async sendMessageViaApiMessage(params: {
    content: string;
    model: string;
    conversationid?: string;
  }): Promise<{ content: string; conversationid?: string; chatid?: string }>
  {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/message");
    }

    const messageId = generateId();
    const chatId = generateId();

    const resp = await api.post<ApiMessageResponse>(
      "/api/message",
      {
        content: params.content,
        model: params.model,
        conversationid: params.conversationid,
        id: messageId,
        chatid: chatId,
      },
      {
        timeout: 120000,
        headers: {
          Authorization: authHeaderValue,
        },
        withCredentials: true,
      }
    );

    const data = resp.data || {};
    const content =
      data.content ??
      data.response ??
      (typeof data === "string" ? data : JSON.stringify(data));

    return {
      content,
      conversationid: data.conversationid ?? params.conversationid,
      chatid: data.chatid,
    };
  },

  async uploadFileViaApiFileUpload(params: {
    file: File;
    model: string;
    conversationid: string;
  }): Promise<{ content?: string; conversationid: string; chatid: string; raw?: any }>
  {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/file_upload");
    }

    const chatId = generateId();

    const form = new FormData();
    form.append("file", params.file);
    form.append("conversationid", params.conversationid);
    form.append("model", params.model);
    form.append("id", chatId);

    const resp = await api.post<ApiFileUploadResponse>("/api/file_upload", form, {
      timeout: 120000,
      headers: {
        Authorization: authHeaderValue,
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    });

    const data: any = resp?.data;
    const content =
      data?.content ??
      data?.response ??
      (typeof data === "string" ? data : undefined);

    return {
      content,
      conversationid: (data?.conversationid as string) ?? params.conversationid,
      chatid: (data?.chatid as string) ?? chatId,
      raw: data,
    };
  },

  async downloadUploadedFileViaApiFileDownload(params: {
    fileid: string;
  }): Promise<{ blob: Blob }>
  {
    const tokenResp = await fetchAmsToken();
    const authHeaderValue: string | undefined = tokenResp?.data?.token;
    if (!authHeaderValue) {
      throw new Error("No auth token available for /api/file_download");
    }

    const resp = await api.get<Blob>(`/api/file_download/${encodeURIComponent(params.fileid)}`,
      {
        timeout: 120000,
        headers: {
          Authorization: authHeaderValue,
        },
        withCredentials: true,
        responseType: "blob",
      }
    );

    return { blob: resp.data as Blob };
  },

  async sendMessage(messages: ChatMessage[]): Promise<string> {
    const response = await api.post("/api/chat/completion", {
      messages,
      stream: false,
    });
    return response.data.content;
  },

  async streamCompletion(
    messages: ChatMessage[],
    onChunk: (chunk: string) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const baseUrl = api.defaults.baseURL || "";
    const url = `${baseUrl}/api/chat/completion`;

    const apiKey = localStorage.getItem("secai_api_key") || "dev-api-key";
    const sessionToken = localStorage.getItem("secai_session_token");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": apiKey,
          ...(sessionToken ? { "X-Session-Token": sessionToken } : {}),
        },
        body: JSON.stringify({
          messages,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Response body is not readable");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            try {
              const event = JSON.parse(data);
              if (event.type === "content" && event.content) {
                onChunk(event.content);
              } else if (event.type === "error") {
                throw new Error(event.error);
              }
            } catch (e) {
              // ignore parse errors
            }
          }
        }
      }
    } catch (error) {
      console.error("Stream error:", error);
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    }
  },

  async getSessions(projectId?: string): Promise<any[]> {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get("/api/chat/sessions", { params });
    return response.data;
  },

  async getSessionMessages(sessionId: string): Promise<ChatMessage[]> {
    const response = await api.get(`/api/chat/messages/${sessionId}`);
    return response.data;
  },
};

export default chatService;
